import { t } from '@lingui/core/macro';
import { formatToLongDate } from '@luminovo/frontend-utils';
import { differenceInCalendarDays } from 'date-fns';

import { createDateValidator } from '../../FieldDate/FieldDateControlled';

export interface DateRangeValue {
    from: string | null;
    to: string | null;
}

export interface CreateDateRangeValidatorOptions {
    required?: boolean;
    minDate?: string;
    maxDate?: string;
    /** Inclusive minimum number of calendar days between `from` and `to`. */
    minDurationDays?: number;
    /** Inclusive maximum number of calendar days between `from` and `to`. */
    maxDurationDays?: number;
    /** Valid only with both sides filled or neither; `required` already demands both. */
    requireBothOrNeither?: boolean;
    validate?: (range: DateRangeValue) => string | undefined;
}

/** `createDateValidator` per side, plus `required`, `to >= from` and the duration bounds. */
export function createDateRangeValidator(opts: CreateDateRangeValidatorOptions = {}) {
    const { required, minDate, maxDate, minDurationDays, maxDurationDays, requireBothOrNeither, validate } = opts;
    const sideValidator = createDateValidator({ minDate, maxDate });

    return (x: unknown): string | undefined => {
        if (!isRangeValue(x)) {
            if (!required) return undefined;
            return t`Required`;
        }

        const { from, to } = x;

        if (!required && from === null && to === null) {
            return undefined;
        }

        if (required && (from === null || to === null)) {
            return t`Required`;
        }

        if (!required && requireBothOrNeither && (from === null) !== (to === null)) {
            return t`Both start and end date are required`;
        }

        const fromError = sideValidator(from);
        if (fromError) {
            return t`Start: ${fromError}`;
        }
        const toError = sideValidator(to);
        if (toError) {
            return t`End: ${toError}`;
        }

        if (from && to) {
            const fromDate = new Date(from);
            const toDate = new Date(to);
            if (toDate < fromDate) {
                return t`End date must be on or after ${formatToLongDate(from)}`;
            }

            const diff = differenceInCalendarDays(toDate, fromDate);
            if (minDurationDays !== undefined && diff < minDurationDays) {
                return t`Range must span at least ${minDurationDays} days`;
            }
            if (maxDurationDays !== undefined && diff > maxDurationDays) {
                return t`Range must span at most ${maxDurationDays} days`;
            }
        }

        if (validate) {
            return validate(x);
        }

        return undefined;
    };
}

function isRangeValue(x: unknown): x is DateRangeValue {
    return (
        typeof x === 'object' &&
        x !== null &&
        'from' in x &&
        'to' in x &&
        ((x as DateRangeValue).from === null || typeof (x as DateRangeValue).from === 'string') &&
        ((x as DateRangeValue).to === null || typeof (x as DateRangeValue).to === 'string')
    );
}
